import React, { useMemo } from 'react';
import { Box, Card, Skeleton, Typography } from '@mui/material';
import { LineChart } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { TokenUsagePeriod, TokenUsagePoint } from '../../types';
import { formatTokens } from './dashboard-format';

const TOKEN_USAGE_PERIODS: TokenUsagePeriod[] = ['day', 'month', 'year'];
const CHART_HEIGHT = 240;
const CHART_TOP = 44;
const CHART_BOTTOM = 196;
const CHART_PADDING_X = 56;
const POINT_SPACING = 96;

interface TokenUsageChartProps {
  period: TokenUsagePeriod;
  points: TokenUsagePoint[];
  loading: boolean;
  error: string | null;
  onPeriodChange: (period: TokenUsagePeriod) => void;
}

export const TokenUsageChart: React.FC<TokenUsageChartProps> = ({
  period,
  points,
  loading,
  error,
  onPeriodChange,
}) => {
  const { t } = useTranslation();
  const chartPoints = useMemo(
    () => points.map((point) => ({
        ...point,
        // API cũ chưa trả totalCredits → fallback totalTokens để dashboard không crash
        totalCredits: point.totalCredits ?? point.totalTokens ?? 0,
        label: formatPointLabel(point, period),
      })),
    [period, points]
  );
  const chartMax = useMemo(
    () => Math.max(1, ...chartPoints.map((point) => point.totalCredits)),
    [chartPoints]
  );
  const periodTotal = chartPoints.reduce((sum, point) => sum + point.totalCredits, 0);
  const chartWidth = useMemo(
    () => Math.max(620, Math.max(chartPoints.length - 1, 1) * POINT_SPACING + CHART_PADDING_X * 2),
    [chartPoints.length]
  );
  const coordinates = useMemo(
    () =>
      chartPoints.map((point, index) => {
        const x =
          chartPoints.length === 1 ? chartWidth / 2 : CHART_PADDING_X + index * POINT_SPACING;
        const y = CHART_BOTTOM - (point.totalCredits / chartMax) * (CHART_BOTTOM - CHART_TOP);
        return { x, y, point };
      }),
    [chartMax, chartPoints, chartWidth]
  );
  const linePath = coordinates
    .map(({ x, y }, index) => `${index === 0 ? 'M' : 'L'} ${x} ${y}`)
    .join(' ');

  return (
    <Card
      className="mb-6 border border-gray-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-800 md:mb-8"
      elevation={0}
    >
      <Box className="mb-5 flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <Box>
          <Typography variant="h6" className="font-semibold text-gray-900 dark:text-white">
            {t('dashboard.tokenUsage.title')}
          </Typography>
          {loading ? (
            <Skeleton variant="text" width={220} height={24} className="mt-1" />
          ) : (
            <Typography variant="body2" className="mt-1 text-gray-500 dark:text-slate-400">
              {t('dashboard.tokenUsage.subtitle', { total: formatTokens(periodTotal) })}
            </Typography>
          )}
        </Box>
        <Box
          role="tablist"
          aria-label={t('dashboard.tokenUsage.periodLabel')}
          className="inline-flex w-fit rounded-lg border border-gray-200 bg-gray-50 p-1 dark:border-slate-700 dark:bg-slate-900"
        >
          {TOKEN_USAGE_PERIODS.map((item) => {
            const isSelected = period === item;
            return (
              <button
                key={item}
                type="button"
                aria-pressed={isSelected}
                onClick={() => onPeriodChange(item)}
                className={`min-w-16 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                  isSelected
                    ? 'bg-white text-blue-700 shadow-sm dark:bg-slate-700 dark:text-blue-300'
                    : 'text-gray-600 hover:text-gray-900 dark:text-slate-400 dark:hover:text-white'
                }`}
              >
                {t(`dashboard.tokenUsage.periods.${item}`)}
              </button>
            );
          })}
        </Box>
      </Box>

      {loading && <TokenUsageChartSkeleton />}
      {!loading && error && (
        <Box className="flex h-72 items-center justify-center rounded-lg border border-dashed border-red-200 bg-red-50 dark:border-red-900/50 dark:bg-red-950/20">
          <Typography variant="body2" className="text-red-600 dark:text-red-300">
            {error}
          </Typography>
        </Box>
      )}
      {!loading && !error && (
        <Box className="min-w-0">
          <Box className="mb-3 flex items-center gap-2">
            <LineChart size={18} className="text-blue-600 dark:text-blue-400" />
            <Typography variant="subtitle2" className="font-semibold text-gray-900 dark:text-white">
              {t('dashboard.tokenUsage.lineTitle')}
            </Typography>
          </Box>
          <Box className="overflow-x-auto rounded-lg border border-gray-100 p-4 dark:border-slate-700">
            <Box style={{ minWidth: `${chartWidth}px` }}>
              <svg
                viewBox={`0 0 ${chartWidth} ${CHART_HEIGHT}`}
                className="h-64 w-full overflow-visible"
                role="img"
                aria-label={t('dashboard.tokenUsage.lineTitle')}
              >
                {[CHART_BOTTOM, (CHART_TOP + CHART_BOTTOM) / 2, CHART_TOP].map((y) => (
                  <line
                    key={y}
                    x1="0"
                    y1={y}
                    x2={chartWidth}
                    y2={y}
                    stroke="currentColor"
                    className="text-gray-100 dark:text-slate-800"
                    strokeWidth="1"
                  />
                ))}
                {linePath && (
                  <path
                    d={linePath}
                    fill="none"
                    stroke="#3B82F6"
                    strokeWidth="3"
                    vectorEffect="non-scaling-stroke"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                )}
                {coordinates.map(({ x, y, point }) => (
                  <g key={point.period}>
                    <text
                      x={x}
                      y={Math.max(16, y - 12)}
                      textAnchor="middle"
                      fontSize="11"
                      fontWeight="600"
                      fill="currentColor"
                      className="text-gray-700 dark:text-slate-200"
                    >
                      {formatTokens(point.totalCredits)}
                    </text>
                    <circle cx={x} cy={y} r="4" fill="#ffffff" stroke="#3B82F6" strokeWidth="2.5">
                      <title>{`${point.label}: ${formatTokens(point.totalCredits)}`}</title>
                    </circle>
                  </g>
                ))}
                {coordinates.map(({ x, point }, index) => {
                  const shouldShow =
                    coordinates.length <= 6 ||
                    index === 0 ||
                    index === coordinates.length - 1 ||
                    index % Math.ceil(coordinates.length / 4) === 0;
                  return shouldShow ? (
                    <text
                      key={`${point.period}-label`}
                      x={x}
                      y={CHART_HEIGHT - 12}
                      textAnchor="middle"
                      fontSize="11"
                      fill="currentColor"
                      className="text-gray-500 dark:text-slate-400"
                    >
                      {point.label}
                    </text>
                  ) : null;
                })}
              </svg>
            </Box>
          </Box>
        </Box>
      )}
    </Card>
  );
};

function formatPointLabel(point: TokenUsagePoint, period: TokenUsagePeriod): string {
  if (period === 'year') return point.period || point.label;
  const [year, month, day] = point.period.split('-');
  if (period === 'month' && year && month) return `${month}/${year}`;
  if (period === 'day' && month && day) return `${day}/${month}`;
  return point.label;
}

const TokenUsageChartSkeleton: React.FC = () => (
  <Box aria-hidden="true" className="min-w-0">
    <Box className="mb-3 flex items-center gap-2">
      <Skeleton variant="circular" width={18} height={18} />
      <Skeleton variant="text" width={160} height={24} />
    </Box>
    <Box className="overflow-x-auto rounded-lg border border-gray-100 p-4 dark:border-slate-700">
      <Box className="min-w-[620px]">
        <Skeleton variant="rectangular" height={260} className="rounded-lg" />
      </Box>
    </Box>
  </Box>
);
